import { ART_PAL, ball, body, fade, mix, n, rng, shade, star, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Spaceport scene behind the game zone (the SVG fallback until the raster scene is there): open space — a star field
 * (a lavender dawn sky in light), a ringed planet top right, a small moon and a far nebula glow top left, the curve of
 * a planet surface at the bottom with a small rocket on its pad on the left and dish antenna on the right. The centre
 * stays calm (the reels sit there): no stars there, only a soft pool. No towers or beams across the panels.
 * Motifs are marked `data-motif="<x>"` (tests). No filters, no text.
 */
export function spaceportScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `sp${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  const sky = P.dark ? mix(P.bg, P.purple, 0.16) : mix(P.purple, '#FFFFFF', 0.82);
  const skyMid = P.dark ? mix(P.bg, P.blue, 0.1) : mix(P.blue, '#FFFFFF', 0.86);
  const skyLow = P.dark ? mix(P.bg, P.purple, 0.08) : mix(P.pink, '#FFFFFF', 0.84);
  const ground = P.dark ? mix(P.bg, P.purple, 0.14) : mix(P.purple, '#FFFFFF', 0.6);
  const planet = P.dark ? shade(P.orange, 0.2) : tint(P.orange, 0.25);
  const ring = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.5);
  const moon = P.dark ? mix(P.sky, '#FFFFFF', 0.5) : tint(P.sky, 0.45);
  const hull = P.dark ? mix(P.ink, P.bg, 0.25) : '#FFFFFF';
  const fin = P.dark ? shade(P.red, 0.1) : tint(P.red, 0.1);
  const starC = P.dark ? '#FFF6DA' : P.purple;

  const defs =
    `<defs>${vgrad(`${p}sk`, sky, skyMid, skyLow)}` +
    ball(`${p}pl`, planet, 0.4, 0.45) +
    ball(`${p}mo`, moon, 0.45, 0.4) +
    fade(`${p}nb`, P.dark ? P.pink : P.purple, P.dark ? 0.22 : 0.2) +
    fade(`${p}nb2`, P.dark ? P.blue : P.sky, P.dark ? 0.22 : 0.18) +
    vgrad(`${p}gr`, ground, shade(ground, 0.2), shade(ground, 0.35)) +
    body(`${p}hl`, hull, 0.2, 0.3, 1, 0.2) +
    fade(`${p}fl`, P.orange, 0.7) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.45 : 0.5) +
    `</defs>`;

  /** star field: deterministic, only outside the middle 40% of the width */
  const stars = (count: number, seed: number) => {
    const r = rng(seed);
    let s = '';
    for (let i = 0; i < count; i++) {
      const side = i % 2;
      const x = side ? W * (0.72 + r() * 0.28) : W * r() * 0.28;
      const y = r() * H * 0.72;
      const big = r() > 0.85;
      s += big
        ? `<polygon points="${star(x, y, 3, 1, 4)}"/>`
        : `<circle cx="${n(x)}" cy="${n(y)}" r="${n(0.8 + r() * 1.2)}"/>`;
    }
    return `<g fill="${starC}" fill-opacity="${o(0.7, 0.35)}">${s}</g>`;
  };
  const ringed = (x: number, y: number, r: number) =>
    `<g data-motif="${Math.round(x)}"><path d="M${n(x - r * 1.9)} ${y}A${n(r * 1.9)} ${n(r * 0.42)} -12 0 0 ${n(x + r * 1.9)} ${y}" transform="rotate(-12 ${x} ${y})" fill="none" stroke="${ring}" stroke-opacity=".75" stroke-width="${n(r * 0.14)}"/>` +
    `<circle cx="${x}" cy="${y}" r="${r}" fill="url(#${p}pl)"/>` +
    `<path d="M${n(x - r * 1.9)} ${y}A${n(r * 1.9)} ${n(r * 0.42)} -12 0 1 ${n(x + r * 1.9)} ${y}" transform="rotate(-12 ${x} ${y})" fill="none" stroke="${ring}" stroke-width="${n(r * 0.14)}"/></g>`;
  const rocket = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${s})">` +
    `<path d="M-12 -10L-26 14V20L-12 12ZM12 -10L26 14V20L12 12Z" fill="${fin}"/>` +
    `<path d="M0 -84C16 -64 16 -30 13 12H-13C-16 -30 -16 -64 0 -84Z" fill="url(#${p}hl)"/>` +
    `<circle cy="-40" r="6" fill="${P.dark ? P.sky : shade(P.sky, 0.1)}"/>` +
    `<path d="M0 -84C6 -77 9 -70 11 -62H-11C-9 -70 -6 -77 0 -84Z" fill="${fin}"/>` +
    `<path d="M-40 20H40V28H-40Z" fill="${shade(ground, 0.4)}"/></g>`;
  const dish = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${s})" fill="${P.dark ? mix(P.ink, P.bg, 0.45) : tint(P.slate, 0.55)}">` +
    `<path d="M-6 0L0 -30L6 0Z"/><path d="M-34 -40A34 34 0 0 0 30 -12Z" transform="rotate(-20 0 -30)"/>` +
    `<path d="M0 -30L18 -52" stroke="${P.dark ? mix(P.ink, P.bg, 0.45) : tint(P.slate, 0.55)}" stroke-width="3"/></g>`;

  const gY = wide ? 790 : 720;
  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}sk)"/>` +
    `<ellipse cx="${n(W * 0.16)}" cy="${n(H * 0.3)}" rx="${n(W * 0.2)}" ry="${n(H * 0.24)}" fill="url(#${p}nb)"/>` +
    `<ellipse cx="${n(W * 0.86)}" cy="${n(H * 0.55)}" rx="${n(W * 0.18)}" ry="${n(H * 0.22)}" fill="url(#${p}nb2)"/>` +
    stars(wide ? 70 : 26, P.dark ? 7 : 11) +
    `<ellipse cx="${W / 2}" cy="${H * 0.5}" rx="${n(W * 0.36)}" ry="${n(H * 0.42)}" fill="url(#${p}calm)"/>` +
    // a planet surface: one wide low curve (under the control panel)
    `<ellipse cx="${W / 2}" cy="${n(gY + W * 0.9)}" rx="${n(W * 1.1)}" ry="${n(W * 0.9)}" fill="url(#${p}gr)"/>`;

  if (wide) {
    art +=
      ringed(1350, 210, 32) +
      `<circle data-motif="270" cx="270" cy="170" r="18" fill="url(#${p}mo)"/>` +
      rocket(280, gY - 26, 1.2) +
      `<circle cx="280" cy="${gY + 6}" r="40" fill="url(#${p}fl)" fill-opacity="${o(0.5, 0.3)}"/>` +
      dish(1330, gY - 10, 1.1);
  } else {
    art +=
      ringed(320, 110, 28) +
      `<circle data-motif="50" cx="50" cy="110" r="14" fill="url(#${p}mo)"/>`;
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Spaceport: running lights with a tiny star (repeat-x tile, 48 × 12). */
export function spaceportAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const a = P.dark ? P.purple : shade(P.purple, 0.05);
  const b = P.gold;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 12" width="48" height="12">` +
    `<path d="M0 3H48" stroke="${a}" stroke-opacity=".5"/>` +
    `<circle cx="12" cy="6.5" r="1.6" fill="${a}"/><circle cx="36" cy="6.5" r="1.6" fill="${a}" fill-opacity=".6"/>` +
    `<polygon points="${star(24, 7, 3.2, 1, 4)}" fill="${b}"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'spaceport', scene: spaceportScene, accent: spaceportAccent });
